/**
 * Recomendaciones de comercio («Bono Pionero») — espejo de
 * `src/modules/merchant-recommendation` del backend.
 *
 * Los seis primeros métodos son los del pipeline compartido, idénticos a los
 * de solicitudes de integración. Los tres últimos son propios: confirmar la
 * activación, consultar los tramos y el informe del programa.
 */

import { apiClient } from './api.service';
import type {
  MerchantRecommendation,
  MerchantRecommendationQueryParams,
  MerchantRecommendationPaginatedResponse,
  ConfirmActivationResult,
  PioneerBonus,
  PioneerReport,
} from '@/types/merchant-recommendation.types';
import type {
  AssignMerchantLeadDto,
  UpdateMerchantLeadStatusDto,
  AddMerchantLeadNoteDto,
} from '@/types/merchant-lead.types';

class MerchantRecommendationService {
  private readonly basePath = '/recommendations';

  async getAll(
    params: MerchantRecommendationQueryParams = {},
    opts: { signal?: AbortSignal } = {}
  ): Promise<MerchantRecommendationPaginatedResponse> {
    const sp = new URLSearchParams();
    if (params.page) sp.set('page', String(params.page));
    if (params.limit) sp.set('limit', String(params.limit));
    if (params.status) sp.set('status', params.status);
    if (params.search) sp.set('search', params.search);
    if (params.assignedToStaffId)
      sp.set('assignedToStaffId', params.assignedToStaffId);
    if (params.pendingOnly) sp.set('pendingOnly', 'true');
    if (params.category) sp.set('category', params.category);
    if (params.cityId) sp.set('cityId', params.cityId);
    if (params.includeDuplicates) sp.set('includeDuplicates', 'true');

    const query = sp.toString();
    const endpoint = query ? `${this.basePath}?${query}` : this.basePath;

    const response =
      await apiClient.get<MerchantRecommendationPaginatedResponse>(endpoint, {
        signal: opts.signal,
      });
    return response.data;
  }

  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {}
  ): Promise<MerchantRecommendation> {
    const response = await apiClient.get<MerchantRecommendation>(
      `${this.basePath}/${id}`,
      { signal: opts.signal }
    );
    return response.data;
  }

  async assign(
    id: string,
    dto: AssignMerchantLeadDto
  ): Promise<MerchantRecommendation> {
    const response = await apiClient.patch<MerchantRecommendation>(
      `${this.basePath}/${id}/assign`,
      dto
    );
    return response.data;
  }

  async updateStatus(
    id: string,
    dto: UpdateMerchantLeadStatusDto
  ): Promise<MerchantRecommendation> {
    const response = await apiClient.patch<MerchantRecommendation>(
      `${this.basePath}/${id}/status`,
      dto
    );
    return response.data;
  }

  async addNote(
    id: string,
    dto: AddMerchantLeadNoteDto
  ): Promise<MerchantRecommendation> {
    const response = await apiClient.post<MerchantRecommendation>(
      `${this.basePath}/${id}/notes`,
      dto
    );
    return response.data;
  }

  /**
   * Vincula la recomendación a una Company existente. No crea la empresa ni
   * paga el bono: el primer tramo se libera cuando la empresa queda activa.
   */
  async convert(
    id: string,
    companyId: string
  ): Promise<MerchantRecommendation> {
    const response = await apiClient.post<MerchantRecommendation>(
      `${this.basePath}/${id}/convert`,
      { companyId }
    );
    return response.data;
  }

  /**
   * Confirma a mano que el comercio quedó activo.
   *
   * Idempotente: si el tramo ya estaba pagado lo dice y no vuelve a pagar, así
   * que el botón no es peligroso.
   */
  async confirmActivation(id: string): Promise<ConfirmActivationResult> {
    const response = await apiClient.post<ConfirmActivationResult>(
      `${this.basePath}/${id}/confirm-activation`
    );
    return response.data;
  }

  async getBonus(
    id: string,
    opts: { signal?: AbortSignal } = {}
  ): Promise<PioneerBonus> {
    const response = await apiClient.get<PioneerBonus>(
      `${this.basePath}/${id}/bonus`,
      { signal: opts.signal }
    );
    return response.data;
  }

  async getReport(
    params: { from?: string; to?: string; cityId?: string } = {},
    opts: { signal?: AbortSignal } = {}
  ): Promise<PioneerReport> {
    const sp = new URLSearchParams();
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (params.cityId) sp.set('cityId', params.cityId);

    const query = sp.toString();
    const response = await apiClient.get<PioneerReport>(
      query ? `${this.basePath}/report?${query}` : `${this.basePath}/report`,
      { signal: opts.signal }
    );
    return response.data;
  }
}

export const merchantRecommendationService =
  new MerchantRecommendationService();
